/**
 * Off-chain spread time-series built from GeckoTerminal's public OHLCV API.
 *
 * The bot's spread is observable per-poll in logs; this module pulls the same
 * signal as a historical series for visualization:
 *
 *   priceBusd     = close price of the ELE/BUSD pool (BUSD per ELE)
 *   priceViaWbnb  = close price of the ELE/WBNB pool × WBNB/BUSD bridge close
 *   spreadPct     = (priceViaWbnb − priceBusd) / min(priceBusd, priceViaWbnb) × 100
 *
 * Signed — positive means ELE is cheaper in pairBUSD (bot's DIR 0),
 * negative means ELE is cheaper in pairWBNB (bot's DIR 1).
 *
 * No auth; public API, 30 req/min free tier. Each call here fires 3 parallel
 * requests, so a 60s refresh is well inside the cap.
 */

// PancakeSwap V2 pool addresses on BSC
const POOL_ELE_BUSD = "0x647bc907d520C3f63bE38d01DBd979f5606beC48";
const POOL_ELE_WBNB = "0x1CEa83EC5E48D9157fCAe27a19807BeF79195Ce1";
const POOL_WBNB_BUSD = "0x58F876857a02D6762E0101bb5C46A8c1ED44Dc16";

// In dev, route through the Vite proxy (see vite.config.ts) — GeckoTerminal
// does not send CORS headers for localhost. In prod the browser hits the
// public API directly.
const GECKO_BASE = import.meta.env.DEV
  ? "/api/gecko/networks/bsc/pools"
  : "https://api.geckoterminal.com/api/v2/networks/bsc/pools";

export type Timeframe = "minute" | "hour" | "day";

export interface SpreadPoint {
  /** Unix seconds at the start of the bucket. */
  ts: number;
  /** ELE/BUSD close for the bucket (BUSD per ELE). */
  priceBusd: number;
  /** ELE/WBNB × WBNB/BUSD close (BUSD per ELE, routed through WBNB). */
  priceViaWbnb: number;
  /** Signed spread, in percent. */
  spreadPct: number;
}

export interface SpreadSeries {
  timeframe: Timeframe;
  points: SpreadPoint[];
  /** Most recent point (convenience; === points[points.length - 1]). */
  latest: SpreadPoint | null;
}

/**
 * GeckoTerminal OHLCV tuple: [timestamp, open, high, low, close, volume_usd].
 * Only `close` is used downstream. Returned as Map<timestamp, close> for O(1)
 * join across the three pools.
 */
type CloseByTs = Map<number, number>;

// GeckoTerminal free tier is 30 req/min per IP. Each fetchSpreadSeries fires
// 3 parallel calls, and StrictMode double-mounts effects in dev, so without
// dedup a remount + interval tick can easily burst past the cap. Cache by
// (pool, timeframe, limit) with a TTL keyed off bucket size, and dedupe
// concurrent calls so the second caller awaits the first.
const TTL_MS_BY_TIMEFRAME: Record<Timeframe, number> = {
  minute: 30_000,
  hour: 5 * 60_000,
  day: 30 * 60_000,
};

const cache = new Map<string, { expires: number; data: CloseByTs }>();
const inflight = new Map<string, Promise<CloseByTs>>();

async function fetchOhlcvCloses(
  pool: string,
  timeframe: Timeframe,
  limit: number,
): Promise<CloseByTs> {
  const key = `${pool}|${timeframe}|${limit}`;
  const now = Date.now();

  const cached = cache.get(key);
  if (cached && cached.expires > now) return cached.data;

  const existing = inflight.get(key);
  if (existing) return existing;

  const url = `${GECKO_BASE}/${pool}/ohlcv/${timeframe}?aggregate=1&limit=${limit}&currency=token`;
  const promise = (async () => {
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) {
        throw new Error(`GeckoTerminal ${pool.slice(0, 10)}… ${res.status} ${res.statusText}`);
      }
      const body = (await res.json()) as {
        data?: { attributes?: { ohlcv_list?: Array<[number, number, number, number, number, number]> } };
      };
      const list = body?.data?.attributes?.ohlcv_list ?? [];
      const map: CloseByTs = new Map();
      for (const row of list) {
        if (!Array.isArray(row) || row.length < 5) continue;
        const [ts, , , , close] = row;
        if (typeof ts === "number" && typeof close === "number" && close > 0) {
          map.set(ts, close);
        }
      }
      cache.set(key, { expires: Date.now() + TTL_MS_BY_TIMEFRAME[timeframe], data: map });
      return map;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

// Largest key <= ts via binary search; undefined if no such key exists.
function lastAtOrBefore(
  sortedKeys: number[],
  values: CloseByTs,
  ts: number,
): number | undefined {
  if (sortedKeys.length === 0) return undefined;
  let lo = 0;
  let hi = sortedKeys.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (sortedKeys[mid] <= ts) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans >= 0 ? values.get(sortedKeys[ans]) : undefined;
}

export async function fetchSpreadSeries(
  timeframe: Timeframe,
  limit: number,
): Promise<SpreadSeries> {
  const [eleBusd, eleWbnb, wbnbBusd] = await Promise.all([
    fetchOhlcvCloses(POOL_ELE_BUSD, timeframe, limit),
    fetchOhlcvCloses(POOL_ELE_WBNB, timeframe, limit),
    fetchOhlcvCloses(POOL_WBNB_BUSD, timeframe, limit),
  ]);

  // GeckoTerminal returns a bucket only when the pool actually traded in
  // that interval, so the three pools' timestamps barely overlap — a naive
  // inner-join produces ~zero points at minute resolution. Sample at the
  // union of all bucket timestamps and forward-fill each pool's most
  // recent close at or before each sample.
  const eleBusdKeys = [...eleBusd.keys()].sort((a, b) => a - b);
  const eleWbnbKeys = [...eleWbnb.keys()].sort((a, b) => a - b);
  const wbnbBusdKeys = [...wbnbBusd.keys()].sort((a, b) => a - b);

  const allTs = new Set<number>();
  for (const k of eleBusdKeys) allTs.add(k);
  for (const k of eleWbnbKeys) allTs.add(k);
  for (const k of wbnbBusdKeys) allTs.add(k);
  const sortedTs = [...allTs].sort((a, b) => a - b);

  // Skip samples before every pool has at least one observation.
  const minTs = Math.max(
    eleBusdKeys[0] ?? Infinity,
    eleWbnbKeys[0] ?? Infinity,
    wbnbBusdKeys[0] ?? Infinity,
  );

  const points: SpreadPoint[] = [];
  for (const ts of sortedTs) {
    if (ts < minTs) continue;
    const priceBusd = lastAtOrBefore(eleBusdKeys, eleBusd, ts);
    const eleWbnbClose = lastAtOrBefore(eleWbnbKeys, eleWbnb, ts);
    const wbnbBusdClose = lastAtOrBefore(wbnbBusdKeys, wbnbBusd, ts);
    if (priceBusd === undefined || eleWbnbClose === undefined || wbnbBusdClose === undefined) continue;
    const priceViaWbnb = eleWbnbClose * wbnbBusdClose;
    const denom = Math.min(priceBusd, priceViaWbnb);
    if (denom <= 0) continue;
    const spreadPct = ((priceViaWbnb - priceBusd) / denom) * 100;
    points.push({ ts, priceBusd, priceViaWbnb, spreadPct });
  }

  return {
    timeframe,
    points,
    latest: points.length > 0 ? points[points.length - 1] : null,
  };
}
